import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Typography } from 'antd';
import { LogoutOutlined, DatabaseOutlined, BarChartOutlined, TeamOutlined, MedicineBoxOutlined, SettingOutlined, HistoryOutlined, StarOutlined } from '@ant-design/icons';
import { useTranslation } from '../../shared/hooks/useTranslation';
import style from './AdminLayout.module.scss';

const { Header, Sider, Content } = Layout;
const { Title } = Typography;

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [selectedKey, setSelectedKey] = useState('dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    if (!adminToken) {
      navigate('/admin/login');
    }
  }, [navigate]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/admin/clients')) {
      setSelectedKey('clients');
    } else if (path.includes('/admin/specialists')) {
      setSelectedKey('specialists');
    } else if (path.includes('/admin/settings')) {
      setSelectedKey('settings');
    } else if (path.includes('/admin/ratings')) {
      setSelectedKey('ratings');
    } else if (path.includes('/admin/statistics')) {
      setSelectedKey('statistics');
    } else if (path.includes('/admin/services-upload-history')) {
      setSelectedKey('uploadHistory');
    } else if (path.includes('/admin/dashboard')) {
      setSelectedKey('services');
    } else {
      setSelectedKey('dashboard');
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const menuItems = [
    {
      key: 'services',
      icon: <DatabaseOutlined />,
      label: t.adminNavServices,
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      key: 'uploadHistory',
      icon: <HistoryOutlined />,
      label: t.adminNavUploadHistory,
      onClick: () => navigate('/admin/services-upload-history'),
    },
    {
      key: 'clients',
      icon: <TeamOutlined />,
      label: t.adminNavClients,
      onClick: () => navigate('/admin/clients'),
    },
    {
      key: 'specialists',
      icon: <MedicineBoxOutlined />,
      label: t.adminNavSpecialists,
      onClick: () => navigate('/admin/specialists'),
    },
    {
      key: 'statistics',
      icon: <BarChartOutlined />,
      label: t.adminNavStatistics,
      onClick: () => navigate('/admin/statistics'),
    },
    {
      key: 'ratings',
      icon: <StarOutlined />,
      label: t.adminNavRatings,
      onClick: () => navigate('/admin/ratings'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t.adminNavSettings,
      onClick: () => navigate('/admin/settings'),
    },
  ];

  return (
    <Layout className={style.adminLayout}>
      <Header className={style.header}>
        <div className={style.headerContent}>
          <Title level={3} style={{ color: 'white', margin: 0 }}>
            {t.adminPanelTitle}
          </Title>
          <Button
            type="text"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            style={{ color: 'white' }}
          >
            {t.adminLogout}
          </Button>
        </div>
      </Header>

      <Layout>
        <Sider width={250} className={style.sider}>
          <Menu
            mode="inline"
            selectedKeys={[selectedKey]}
            items={menuItems}
            inlineIndent={12}
            onClick={({ key }) => setSelectedKey(key)}
            className={style.menu}
          />
        </Sider>

        <Content className={style.mainContent}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}
